<template>
  <div>
    <Layout ref="layoutRef" :selectedOption="2" />
    <h4 class="mb-4">Facturas Agrupadas por Estado</h4>
    <hr>

    <!-- Botones de período -->
    <div class="mb-3 d-flex flex-wrap gap-2">
      <button class="btn btn-sm btn-outline-primary"   @click="setToday">Hoy</button>
      <button class="btn btn-sm btn-outline-secondary" @click="setThisMonth">Mes Actual</button>
      <button class="btn btn-sm btn-outline-success"   @click="setThisQuarter">Trimestre Actual</button>
      <button class="btn btn-sm btn-outline-info"      @click="setThisSemester">Semestre Actual</button>
      <button class="btn btn-sm btn-outline-warning"   @click="setThisYear">Año Actual</button>
    </div>

    <!-- Rango de fechas -->
    <div class="mb-3">
      <div class="input-group">
        <span class="input-group-text">Desde:</span>
        <input type="date" class="form-control" v-model="fechaDesde">
        <span class="input-group-text">Hasta:</span>
        <input type="date" class="form-control" v-model="fechaHasta">
        <button class="btn btn-dark" @click="fetchFacturas" :disabled="cargando">
          <span v-if="cargando" class="spinner-border spinner-border-sm me-1"></span>
          Consultar
        </button>
      </div>
    </div>

    <div v-if="facturasAgrupadas.length">
      <!-- Tabs -->
      <div class="tabs">
        <button class="tablink" :class="{ 'active-tab': tabActivo === 'tabla' }"
                @click="tabActivo = 'tabla'">
          <span class="material-symbols-outlined" style="font-size:17px">table_view</span>
          Tabla
        </button>
        <button class="tablink" :class="{ 'active-tab': tabActivo === 'grafico' }"
                @click="tabActivo = 'grafico'">
          <span class="material-symbols-outlined" style="font-size:17px">pie_chart</span>
          Gráfico
        </button>
      </div>

      <!-- Tab Tabla -->
      <div v-if="tabActivo === 'tabla'" class="mt-3">
        <DataTable
          :data="facturasAgrupadas"
          :columns="columnas"
          idField="estadoFactura"
          :acciones="false"
          :mostrarBotonTodos="false"
          :mostrarBotonNuevo="false"
          :mostrarBotonExcel="true"
          mensajeVacio="No hay facturas para el período seleccionado."
        >
          <!-- Estado con badge -->
<template #cell-estadoFactura="{ row }">
  <span
    class="badge"
    :class="badgeEstado(row.estadoFactura)"
    style="cursor:pointer"
    @click="verDetalle(row.estadoFactura)"
    title="Ver detalle"
  >
    {{ row.estadoFactura }}
  </span>
</template>

          <!-- Cantidad alineada -->
          <template #cell-cantidad="{ row }">
            <span class="d-block text-end">{{ row.cantidad }}</span>
          </template>

          <!-- Total alineado y clickeable -->
<template #cell-total="{ row }">
  <span class="d-block text-end">{{ formatMoneda(row.total) }}</span>
</template>

          <!-- Totales en barra superior -->
          <template #acciones-extra>
            <div class="col-auto d-flex align-items-center gap-3 ms-2">
              <span class="small fw-bold">
                Total facturas: <strong>{{ totalCantidad }}</strong>
              </span>
              <span class="small fw-bold">
                Total monto: <strong>{{ formatMoneda(totalMonto) }}</strong>
              </span>
            </div>
          </template>
        </DataTable>
      </div>

      <!-- Tab Gráfico -->
      <div v-if="tabActivo === 'grafico'" class="mt-3">
        <apexchart
          type="pie"
          :options="chartOptions"
          :series="seriesMonto"
          height="420"
        />
      </div>
    </div>

    <div v-else-if="consultado" class="alert alert-info mt-3">
      No hay datos para el período seleccionado.
    </div>

    <Footer />
  </div>
</template>

<script>
import Layout        from '@/components/Layout.vue'
import Footer        from '@/components/Footer.vue'
import DataTable     from '@/components/DataTable.vue'
import VueApexCharts from 'vue3-apexcharts'
import api           from '@/services/api'

export default {
  components: { Layout, Footer, DataTable, apexchart: VueApexCharts },

  data() {
    return {
      fechaDesde:       '',
      fechaHasta:       '',
      facturas:         [],   // datos crudos del endpoint
      tabActivo:        'tabla',
      cargando:         false,
      consultado:       false,
      seriesMonto:      [],
    }
  },

  computed: {
    // Agrupa las facturas por estadoFactura en el frontend
    facturasAgrupadas() {
      const mapa = {}
      this.facturas.forEach(f => {
        const estado = f.estadoFactura ?? 'SIN ESTADO'
        if (!mapa[estado]) {
          mapa[estado] = { estadoFactura: estado, cantidad: 0, total: 0 }
        }
        mapa[estado].cantidad++
        mapa[estado].total += f.total ?? 0
      })
      return Object.values(mapa).sort((a, b) => a.estadoFactura.localeCompare(b.estadoFactura))
    },

    columnas() {
      return [
        {
          field:         'estadoFactura',
          label:         'Estado',
          filterType:    'select',
          filterOptions: this.facturasAgrupadas
            .map(f => ({ value: f.estadoFactura, label: f.estadoFactura })),
        },
        { field: 'cantidad', label: 'Cantidad', numeric: true },
        { field: 'total',    label: 'Total',    numeric: true },
      ]
    },

    totalCantidad() {
      return this.facturasAgrupadas.reduce((t, f) => t + f.cantidad, 0)
    },

    totalMonto() {
      return this.facturasAgrupadas.reduce((t, f) => t + f.total, 0)
    },

    chartOptions() {
      const colores = {
        PENDIENTE: '#fbbf24',
        EMITIDA:   '#3b82f6',
        PARCIAL:   '#a78bfa',
        PAGADA:    '#10b981',
        ANULADA:   '#f87171',
      }
      return {
        chart:   { type: 'pie', toolbar: { show: false } },
        labels:  this.facturasAgrupadas.map(f => f.estadoFactura),
        colors:  this.facturasAgrupadas.map(f => colores[f.estadoFactura] ?? '#94a3b8'),
        tooltip: {
          y: { formatter: val => this.formatMoneda(val) },
        },
        legend: { position: 'bottom' },
        responsive: [{
          breakpoint: 480,
          options: { chart: { width: 300 }, legend: { position: 'bottom' } },
        }],
      }
    },
  },

  mounted() {
    this.$refs.layoutRef.toggleProcesos()
    this.$refs.layoutRef.toggleReportes()
    this.$refs.layoutRef.toggleParametros()
    this.$refs.layoutRef.toggleAyuda()
  },

  methods: {
    formatMoneda(valor) {
      return new Intl.NumberFormat('es-EC', {
        style:                 'currency',
        currency:              'USD',
        minimumFractionDigits: 2,
      }).format(valor ?? 0)
    },

    badgeEstado(estado) {
      const clases = {
        PENDIENTE: 'badge-pendiente',
        EMITIDA:   'badge-emitida',
        PARCIAL:   'badge-parcial',
        PAGADA:    'badge-pagada',
        ANULADA:   'badge-anulada',
      }
      return clases[estado] ?? 'bg-secondary'
    },

    setPeriod(desde, hasta) {
      this.fechaDesde = desde
      this.fechaHasta = hasta
      this.fetchFacturas()
    },

    setToday() {
      const t = new Date().toISOString().split('T')[0]
      this.setPeriod(t, t)
    },

    setThisMonth() {
      const d = new Date()
      this.setPeriod(
        new Date(d.getFullYear(), d.getMonth(),     1).toISOString().split('T')[0],
        new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().split('T')[0]
      )
    },

    setThisQuarter() {
      const d = new Date(), q = Math.floor(d.getMonth() / 3)
      this.setPeriod(
        new Date(d.getFullYear(), q * 3,     1).toISOString().split('T')[0],
        new Date(d.getFullYear(), q * 3 + 3, 0).toISOString().split('T')[0]
      )
    },

    setThisSemester() {
      const d = new Date(), s = Math.floor(d.getMonth() / 6)
      this.setPeriod(
        new Date(d.getFullYear(), s * 6,     1).toISOString().split('T')[0],
        new Date(d.getFullYear(), s * 6 + 6, 0).toISOString().split('T')[0]
      )
    },

    setThisYear() {
      const y = new Date().getFullYear()
      this.setPeriod(`${y}-01-01`, `${y}-12-31`)
    },

    async fetchFacturas() {
      if (!this.fechaDesde || !this.fechaHasta) return
      this.cargando   = true
      this.consultado = false
      try {
        const idEmpresa = window.sessionStorage.getItem('idEmpresa')
        const { data }  = await api.get(`/vwfacturas/idempresa/${idEmpresa}`, {
          params: { fechaDesde: this.fechaDesde, fechaHasta: this.fechaHasta },
        })
        this.facturas    = data ?? []
        this.seriesMonto = this.facturasAgrupadas.map(f => f.total)
      } catch (e) {
        console.error('Error fetching facturas:', e)
        this.facturas = []
      } finally {
        this.cargando   = false
        this.consultado = true
      }
    },

verDetalle(estadoFactura) {
  this.$router.push({
    name:  'DetalleEstadoFactura',
    params: { p_estadofactura: estadoFactura },
    state: {
      fechaDesde: this.fechaDesde,
      fechaHasta: this.fechaHasta,
    },
  })
},
  },
}
</script>

<style scoped>
.tabs {
  display: flex;
  gap: 4px;
  border-bottom: 2px solid #dee2e6;
  margin-bottom: 0;
}
.tablink {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 18px;
  border: none;
  border-bottom: 3px solid transparent;
  margin-bottom: -2px;
  background: transparent;
  color: #6c757d;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  border-radius: 6px 6px 0 0;
  transition: all 0.2s ease;
}
.tablink:hover  { color: #0d6efd; background: #f0f4ff; }
.active-tab     { color: #0d6efd; border-bottom-color: #0d6efd; background: #fff; font-weight: 600; }

/* Badges de estado */
.badge           { font-size: 0.75rem; padding: 4px 10px; border-radius: 20px; font-weight: 600; }
.badge-pendiente { background: #fef3c7; color: #92400e; }
.badge-emitida   { background: #dbeafe; color: #1e40af; }
.badge-parcial   { background: #ede9fe; color: #5b21b6; }
.badge-pagada    { background: #d1fae5; color: #065f46; }
.badge-anulada   { background: #fee2e2; color: #991b1b; }
</style>